import React, { useCallback, useEffect, useMemo } from 'react'
import ContentHeader from './ContentHeader'
import {
  useI18n,
  usePersonalInfo,
  useTalkAboutCoverage,
  useRehabSelectedThemes,
  useUnavailableStatus,
  useUserProfile,
} from 'contexts/AuthContext'
import { Button } from 'antd'
import CareSelectButton from 'components/CareSelectButton/index'
import {
  CashPayVariants,
  getInsuranceProviderForAmplitude,
  ScreenKey,
  UploadSelectionOptionType,
} from './util'
import { useApplyTags, useMetadataOptions } from 'hooks/careHooks'
import {
  providerSupportPromise,
  uploadSelectionPromise,
} from 'apis/FitOnCareAPI'
import { logBrazeEvent, setBrazeUserAttribute } from 'utils/brazeInit'
import { logAmplitudeEvent, setAmplitudeOption } from 'utils/amplitudeInit'
import { isMobile } from 'utils/basic'
import { getLocalLanguage, getStateId } from 'utils/authLocalStorage'
import _ from 'lodash'
import { isProduction } from 'apis/formatUrl'
import CareInput from 'components/CareInput/index'
import { SkuNamesWithoutRD } from 'hooks/useFlow'
import { UnavailableStatus } from './Unavailable'

const FilterInsuranceProviderSkuNames = SkuNamesWithoutRD

const TalkAboutCoverage = (props) => {
  const { i18n } = useI18n()
  const { talkAboutCoverage, setTalkAboutCoverage } = useTalkAboutCoverage()
  const { insuranceProviders } = useMetadataOptions()
  const { supportForNewWelcome, promo, sku } = props
  const { rehabSelectedThemes } = useRehabSelectedThemes()
  const { applyHealthieTags } = useApplyTags()
  const { personalInfo } = usePersonalInfo()
  const { setUnavailableStatus } = useUnavailableStatus()
  const { getUserProfile } = useUserProfile()

  const skuNotEligible = (sku) => {
    setUnavailableStatus(UnavailableStatus.NOT_ELIGIBLE_TALK_ABOUT_COVERAGE)
    return ScreenKey.UNAVAILABLE
  }

  const { theme } = useMemo(() => {
    return {
      theme: rehabSelectedThemes?.map((theme) => theme.name).join(','),
    }
  }, [rehabSelectedThemes])

  const filteredInsuranceProviders = useMemo(() => {
    const _insuranceProviders =
      _.orderBy(insuranceProviders, ['sort'], ['asc']) || []
    const shouldFilter = FilterInsuranceProviderSkuNames.includes(sku)
    return shouldFilter
      ? _insuranceProviders.filter(
          (item) => item.name !== 'I will not be using insurance'
        )
      : _insuranceProviders
  }, [insuranceProviders, sku])

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - Insurance - Provider', {
      sku: sku,
      theme: theme,
      language: getLocalLanguage(),
    })
  }, [sku, theme])

  const isNextButtonEnable = useMemo(() => {
    return (
      !_.isEmpty(talkAboutCoverage) &&
      (talkAboutCoverage.name !== 'Other' ||
        !_.isEmpty(talkAboutCoverage.otherText))
    )
  }, [talkAboutCoverage])

  const applyRDTag = useCallback(() => {
    const { state } = personalInfo || {}
    if (state.code === 'CA') {
      applyHealthieTags({
        tagId: isProduction() ? '37490' : '3824',
      })
    } else if (state.code === 'NY') {
      applyHealthieTags({
        tagId: isProduction() ? '37487' : '3826',
      })
    } else {
      applyHealthieTags({
        tagId: isProduction() ? '37484' : '3825',
      })
    }
  }, [applyHealthieTags, personalInfo])

  const isProviderSupportFunction = useCallback(
    async (item) => {
      const providerSupport = await providerSupportPromise({
        stateId: getStateId(),
        insuranceProviderId: item.id,
      })
      switch (sku) {
        case 'pt':
          return providerSupport.ptAccept
        case 'wlc':
          return providerSupport.wlcAccept
        default:
          return providerSupport.accept
      }
    },
    [sku]
  )

  const updateClient = async (item) => {
    uploadSelectionPromise({
      optionType: UploadSelectionOptionType.INSURANCE_PROVIDER_OPTION,
      selectedOptionId: item.id,
      otherInputText: item.otherText,
    })
    const isProviderSupport = await isProviderSupportFunction(item)

    if (CashPayVariants.includes(promo)) {
      logAmplitudeEvent('Payment: Promo Code Added', {
        'Promo Code': promo,
        language: getLocalLanguage(),
        sku: props.sku,
      })
      applyRDTag()
      // Proceed to Step 11A “Scheduled Time”
      props.setCurrentKey(ScreenKey.SCHEDULED_TIME)
    } else if (isProviderSupport) {
      if (_.isEmpty(supportForNewWelcome) && _.isEmpty(sku)) {
        logBrazeEvent('User Approved')
      }
      props.setCurrentKey(ScreenKey.INSURANCE_CARD_INFO)
    } else if (item.name === 'I will not be using insurance') {
      // Proceed to Step 11B “Cash Pay”
      props.setCurrentKey(
        sku !== 'rd' ? skuNotEligible(sku) : ScreenKey.CASH_PAY
      )
    } else {
      // Proceed to Step 9A “Insurance Not Accepted”
      props.setUserStatus(6)
      props.setCurrentKey(
        sku !== 'rd' ? skuNotEligible(sku) : ScreenKey.INSURANCE_NOT_ACCEPTED
      )
    }
    setBrazeUserAttribute(
      'Payment Primary',
      item.name === 'I will not be using insurance' ? 'Self Pay' : 'Insurance'
    )
    const provider = getInsuranceProviderForAmplitude(item)
    setBrazeUserAttribute('Insurance Provider', provider)
    logAmplitudeEvent('Click: Onboarding - Insurance - Provider', {
      Provider: provider,
      sku: sku,
      theme: theme,
      language: getLocalLanguage(),
    })
    setAmplitudeOption('Insurance - Provider', provider)
    getUserProfile()
  }

  return (
    <div className='info-sec'>
      <ContentHeader {...props} />
      <div className='info-content'>
        <div className='title'>{i18n("Let's talk about coverage")}</div>
        <div
          className='subtitle'
          style={isMobile() ? { marginBottom: '32px' } : {}}
        >
          {i18n('Who is your insurance provider?')}
        </div>
        {filteredInsuranceProviders.map((item, index) => (
          <div key={index}>
            <CareSelectButton
              key={index}
              {...{
                selected: talkAboutCoverage.id === item.id,
                setSelected: () => {
                  setTalkAboutCoverage({ ...item })
                },
                text: item.name,
                className: `left-align ${isMobile() ? 'm-t-8' : 'm-t-18'}`,
                showEnglishOnly:
                  item.name !== 'Other' &&
                  item.name !== 'I will not be using insurance',
              }}
            />
          </div>
        ))}
        {talkAboutCoverage.name === 'Other' && (
          <CareInput
            {...{
              inputValue: talkAboutCoverage.otherText || '',
              setInputValue: (value) => {
                const regex = /^[a-zA-Z0-9\s]*$/
                if (regex.test(value)) {
                  setTalkAboutCoverage({
                    ...talkAboutCoverage,
                    otherText: value,
                  })
                }
              },
              placeholder: i18n('Please specify'),
              focus: talkAboutCoverage.name === 'Other',
              maxLength: 50,
              className: `input-sec-padding ${isMobile() ? 'm-t-8' : 'm-t-18'}`,
            }}
          />
        )}
        <Button
          className='btn-primary full-width-btn'
          style={isMobile() ? { width: '100%' } : {}}
          onClick={() => {
            updateClient({ ...talkAboutCoverage })
          }}
          disabled={!isNextButtonEnable}
        >
          {i18n('Next')}
        </Button>
      </div>
    </div>
  )
}

export default TalkAboutCoverage
